import type { Role, NpcStage } from '../net/protocol';

// Neutral NPCs are energy batteries, not enemies. They do not have HP and
// never die — when pushed too far they LEAVE (walk off the map edge, or
// one day, step into their house).
//
// Each NPC carries a signed MOOD meter:
//
//   mood < 0  →  NPC feels introverted (extroverts have been absorbing their
//                extroverted energy).  Shouts "LEAVE ME ALONE!" and flees the
//                nearest extrovert.
//   mood > 0  →  NPC feels extroverted (introverts have been absorbing their
//                introverted energy).  Shouts "I'M SO LONELY!" and clings to
//                the nearest introvert.
//
// A player's "attack" is really ENERGY ABSORPTION: standing in your weapon
// shape pulls the NPC's polarity away from neutral. The base influence rate
// is a fixed constant; items/weapons scale it via `absorptionMult`. Every
// tick the meter relaxes toward 0 at NORMALIZATION_PER_SEC (homeostasis),
// so an unattended NPC always drifts back to neutral.
//
// Mood has NO hard cap — it can grow arbitrarily. A saturation factor on
// absorption provides a soft diminishing return (the more drained the NPC,
// the less new energy each tick extracts), and normalization pulls them
// back toward 0 when unattended. There is NO automatic "leave at threshold"
// anymore: the NPC only despawns when it PHYSICALLY crosses the map edge
// as a consequence of its flee/cling motion. This is the skill hook —
// good players herd the NPC by positioning themselves so the NPC's natural
// response walks a useful path (into a corner, away from the edge) and
// keeps farming XP as long as they can hold them in-bounds.

export const NPC_TUNING = {
  COUNT: 10,

  MOOD_SOFT_CAP: 100,               // reference for saturation falloff only — NOT a clamp
  // Playtest 2026-04-17: absorption rate doubled and homeostasis halved
  // so the mood bar visibly fills inside ~1s and "kills" (NPC walking
  // off-map) happen within 4-5s of solid contact instead of ~10s. Stage
  // thresholds pulled in so morphs read sooner.
  BASE_ABSORPTION_PER_SEC: 28,      // was 12 — visible feedback inside 1s
  NORMALIZATION_PER_SEC: 0.035,     // was 0.075 — work persists longer

  STAGE1_AT: 18,                    // was 30
  STAGE2_AT: 45,                    // was 65
  STAGE3_AT: 75,                    // was 95

  XP_PER_ABSORPTION_UNIT: 0.5,      // XP granted per mood-unit absorbed this tick
  SHINY_MULT: 3,
  SHINY_SEC: 20,
  SHINY_CAP: 3,

  CONTACT_R: 1.25,
  CONTACT_DPS: [0, 0, 10, 24] as const,  // indexed by stage
  // Stage 3 = saturation. 2026-04-18 rule: saturated NPCs run at 4×
  // the base walk speed (2.1 → 8.4) so they outrun the player unless
  // you've stacked Sprinter's Legs + Transcendence. Creates real
  // pressure when multiple stage-3 NPCs are on the field.
  SPEED:       [2.1, 3.3, 5.0, 8.4] as const,

  WANDER_R: 6,
  // Matches the Classic 80u arena. CP4+ will derive per-mode from
  // arenaForMode(mode).npcBounds at NpcManager construction.
  SPAWN_BOUNDS: { xMin: -34, xMax: 34, zMin: -34, zMax: 34 },
  LEAVE_MARGIN: 12,                 // NPC is "gone" once physically past bounds + margin
  RESPAWN_DELAY_SEC: 4,

  HOOD_BLOCKS_ABSORPTION: true,     // hooded virgins are invisible to NPC mood
} as const;

// Saturation factor — the more drained the NPC, the less each new tick
// pulls out of them. Natural asymptote around |mood| ≈ 86 with default
// tuning, but positioning skill can push that higher.
export function saturationFactor(mood: number): number {
  return NPC_TUNING.MOOD_SOFT_CAP / (NPC_TUNING.MOOD_SOFT_CAP + Math.abs(mood));
}

export function stageFromMood(mood: number): NpcStage {
  const m = Math.abs(mood);
  if (m < NPC_TUNING.STAGE1_AT) return 0;
  if (m < NPC_TUNING.STAGE2_AT) return 1;
  if (m < NPC_TUNING.STAGE3_AT) return 2;
  return 3;
}

// Shout is driven by the NPC's own feeling (mood polarity), not by whoever
// happens to be nearby at the moment. Variant pools keep the flavor fresh
// across a match — classics (LEAVE ME ALONE / I'M SO LONELY) stay
// weighted high so the core memes still land.
const SHOUTS_ANNOYED = [
  'LEAVE ME ALONE!',
  'LEAVE ME ALONE!',
  'STOP PERCEIVING ME',
  'GO FIND YOUR OWN LIGHT',
  'MY SOCIAL BATTERY IS AT 2%',
  'THIS IS PARASOCIAL',
  'GET A HOBBY',
  'BLOCKED. MUTED. UNFOLLOWED.',
];
const SHOUTS_LONELY = [
  "I'M SO LONELY!",
  "I'M SO LONELY!",
  'PLEASE BE MY FRIEND',
  'PLS NOTICE ME',
  'LIKE AND SUBSCRIBE',
  'WHY WON\'T YOU LOVE ME',
  'MY FINAL FORM IS TOUCH STARVED',
  'DM ME ANYTHING',
];

export function shoutForMood(mood: number): string {
  const pool = mood < 0 ? SHOUTS_ANNOYED : SHOUTS_LONELY;
  return pool[Math.floor(Math.random() * pool.length)];
}

// UI / FX tint matches the faction whose absorption pushed the NPC into
// that state — orange (ext) caused mood<0, blue (int) caused mood>0.
export function causeRole(mood: number): Role {
  return mood < 0 ? 'ext' : 'int';
}
